import { BasePointDragger } from "./BasePointDragger";
import { DraggerType, type Point3D, type TypedInputMode } from "./types";
import {
  RELATIVE_POINT_MODES,
  relativePointValues,
  pointFromRelativeInput,
} from "./relativePointInput";
import { selectionService } from "../selection";
import {
  translationTransform,
  type EntityTransform,
} from "../transform/entityTransform";

/**
 * Interactive "copy to point(s)" tool: pick a base point, then one or more
 * insertion points, each producing an independent clone of the entities that
 * were selected when the tool was activated — AutoCAD COPY-style.
 *
 * The base point never changes: every insertion point is an absolute offset
 * from it, never chained from the previous copy. That falls out of
 * BasePointDragger capturing `originalEntityIds` once on activation.
 */
export class CopyDragger extends BasePointDragger {
  readonly type = DraggerType.Copy;

  protected readonly followUpPointCount = 1;

  /** Keep placing copies until the user ends the command. */
  protected override readonly repeats = true;

  /** The originals must stay visible — they are what is being copied. */
  protected override readonly hidesOriginals = false;

  protected computeTransform(current: Point3D): EntityTransform {
    const base = this.basePoint;
    if (!base) return translationTransform(0, 0);
    return translationTransform(current.x - base.x, current.y - base.y);
  }

  protected commit(transform: EntityTransform): void {
    selectionService.cloneEntitiesAtOffset(
      this.originalEntityIds,
      transform.translation.x,
      transform.translation.y,
    );
  }

  protected promptKey(): string {
    return this.basePoint
      ? "canvas:selection.basePointPrompt.insertion"
      : "canvas:selection.basePointPrompt.base";
  }

  // ===== Typed input: the insertion point, as an X/Y or length/angle offset ===
  //
  // Repeatable like the pointer flow: validating places one copy and leaves the
  // tool (and the pad) up, with the fields still mirroring the offset that was
  // just used — same as the ghosts, which also stay where the last copy landed.

  protected override typedInputModes(): TypedInputMode[] | null {
    return this.basePoint ? RELATIVE_POINT_MODES : null;
  }

  protected override typedInputTitleKey(): string {
    return "canvas:tools.options.keypad.title.copy";
  }

  protected override typedInputValues(
    current: Point3D | null
  ): Record<string, number> {
    return relativePointValues(this.basePoint, current);
  }

  protected override worldPointFromTypedValues(
    modeId: string,
    values: Record<string, number>
  ): Point3D | null {
    return pointFromRelativeInput(this.basePoint, modeId, values);
  }
}
